import { createContext, useContext } from 'react';
import type { BrandProfile, BrandRole, BrandSurface } from '@/types/portal';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

export interface BrandContextValue {
  profile: BrandProfile;
  colours: Record<BrandRole, string>;
  surfaces: BrandSurface[];
  /** Uploaded logo assets keyed by asset id, stored as data URLs. */
  logos: Record<string, string>;
  dirty: boolean;

  updateField: (key: keyof BrandProfile, value: string) => void;
  setMarkets: (markets: string[]) => void;
  applyColour: (role: BrandRole, hex: string) => void;
  applyPreset: (preset: { primary: string; accent: string; secondary: string }) => void;
  toggleSurface: (id: string) => void;
  setLogo: (id: string, dataUrl: string) => void;
  clearLogo: (id: string) => void;
  /** Save current edits to the agency — subsequent discard will return to this snapshot. */
  save: () => Promise<void>;
  /** Discard unsaved edits — restores the last saved snapshot. */
  reset: () => void;
  /** Reset colours, logos and surfaces to the platform defaults (the profile is kept). */
  resetToDefaults: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export const BrandContext = createContext<BrandContextValue | null>(null);

export function useBrand(): BrandContextValue {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used inside BrandProvider');
  return ctx;
}